import { Button, Flex, Input } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useState } from 'react'

import { FileDropzone } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { MaskedInput } from '../../../components/MaskedInput'
import { RichTextField } from '../../../components/RichTextField'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import { ISupportTicketCreatePayload } from '../../../services/Support/interfaces'
import { SupportService } from '../../../services/Support/SupportService'

const MODULE_OPTIONS = [
  { label: 'Dashboard', value: 'Dashboard' },
  { label: 'Agentes de IA', value: 'Agentes de IA' },
  { label: 'Comercial > Base de Leads', value: 'Comercial > Base de Leads' },
  { label: 'Comercial > Consultores Comerciais', value: 'Comercial > Consultores Comerciais' },
  { label: 'Comercial > Feriados e Plantões', value: 'Comercial > Feriados e Plantões' },
  { label: 'Comercial > Black List', value: 'Comercial > Black List' },
  { label: "Comercial > Gerador de UTM's", value: "Comercial > Gerador de UTM's" },
  { label: 'Experiência do Aluno > Solicitações', value: 'Experiência do Aluno > Solicitações' },
  { label: 'Experiência do Aluno > Video Quiz', value: 'Experiência do Aluno > Video Quiz' },
  { label: 'Pedagógico > Entrega de Trabalhos', value: 'Pedagógico > Entrega de Trabalhos' },
  { label: 'Pedagógico > Lista de Concursos', value: 'Pedagógico > Lista de Concursos' },
  { label: 'Pedagógico > Inscrições em Concursos', value: 'Pedagógico > Inscrições em Concursos' },
  { label: 'RH > Solicitações de MP', value: 'RH > Solicitações de MP' },
  { label: 'RH > Vagas de Emprego', value: 'RH > Vagas de Emprego' },
  { label: 'RH > Banco de Talentos', value: 'RH > Banco de Talentos' },
  { label: 'RH > Admissão de Colaborador', value: 'RH > Admissão de Colaborador' },
  { label: 'RH > Colaboradores', value: 'RH > Colaboradores' },
  { label: 'RH > Cadastro de Cargos', value: 'RH > Cadastro de Cargos' },
  { label: 'RH > Acelera Magenta', value: 'RH > Acelera Magenta' },
  { label: 'RH > Brilha Magenta', value: 'RH > Brilha Magenta' },
  { label: 'RH > Conexão Magenta', value: 'RH > Conexão Magenta' },
  { label: 'RH > Avaliação de Experiência', value: 'RH > Avaliação de Experiência' },
  { label: "Marketing > Gerenciador de URL's", value: "Marketing > Gerenciador de URL's" },
  { label: 'Marketing > Snippet de Contato', value: 'Marketing > Snippet de Contato' },
  { label: "Financeiro > Redirecionador de URL's", value: "Financeiro > Redirecionador de URL's" },
  { label: 'Financeiro > Reembolsos', value: 'Financeiro > Reembolsos' },
  { label: 'Website > Banners', value: 'Website > Banners' },
  { label: 'Website > Blog Posts', value: 'Website > Blog Posts' },
  { label: 'Website > Cursos', value: 'Website > Cursos' },
  { label: 'Website > Eventos', value: 'Website > Eventos' },
  { label: 'Website > Professores', value: 'Website > Professores' },
  { label: 'Website > Alunos', value: 'Website > Alunos' },
  { label: 'Administração > Usuários', value: 'Administração > Usuários' },
  { label: 'Administração > Grupos de Usuários', value: 'Administração > Grupos de Usuários' },
  { label: 'Administração > Setores', value: 'Administração > Setores' }
]

interface IProps {
  isOpen: boolean
  onClose: () => void
}

const EMPTY_FORM: ISupportTicketCreatePayload = {
  resume: '',
  phone: '',
  module: '',
  description: '',
  attachments: [] as File[]
}

const EMPTY_ERRORS: Record<string, string[]> = {}

// StoreSupportTicketRequest: `attachments.* => file|mimes:jpg,jpeg,png,webp,pdf|max:5120`. O
// rótulo do campo já anunciava "até 5MB cada", mas nada no código conferia o tamanho — e o
// `accept="image/*"` deixava passar GIF e HEIC, que o backend recusa.
export const SUPPORT_ATTACHMENT_MAX_MB = 5

export default function SupportCreateModal({ isOpen, onClose }: IProps) {
  const toast = useAppToast()
  const supportService = new SupportService()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ISupportTicketCreatePayload>(EMPTY_FORM)
  const [errors, setErrors] = useState<Record<string, string[]>>(EMPTY_ERRORS)
  const [isCustomModule, setIsCustomModule] = useState(false)

  const handleClose = () => {
    setForm(EMPTY_FORM)
    setErrors(EMPTY_ERRORS)
    setIsCustomModule(false)
    onClose()
  }

  const handleSubmit = async () => {
    setSaving(true)
    setErrors(EMPTY_ERRORS)

    try {
      await supportService.create(form)
      toast.success('Chamado criado com sucesso!')
      setForm(EMPTY_FORM)
      setIsCustomModule(false)
      window.dispatchEvent(new Event('support-ticket-created'))
      onClose()
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ errors: Record<string, string[]> }>
      if (axiosError.response?.data.errors) {
        setErrors(axiosError.response.data.errors)
        toast.error('Erro ao criar chamado', 'Verifique os campos destacados e tente novamente.')
      } else {
        toast.error('Erro ao criar chamado', 'Ocorreu um erro inesperado. Tente novamente mais tarde.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Novo Chamado de Suporte"
      onSubmit={handleSubmit}
      confirmLabel="Abrir Chamado"
      isLoading={saving}
    >
      <Flex direction="column" gap={4}>
        {/* resume */}
        <FormField label="Resumo do problema" isRequired error={errors.resume}>
          <Input
            placeholder="Descreva brevemente o problema"
            value={form.resume}
            onChange={(e) => setForm((p) => ({ ...p, resume: e.target.value }))}
          />
        </FormField>

        <Flex gap={4} flexDirection={{ base: 'column', sm: 'row' }}>
          {/* Phone */}
          <FormField label="Telefone de Contato" isRequired error={errors.phone} flex={1}>
            <MaskedInput value={form.phone} mask="phone" onChange={(phone) => setForm((p) => ({ ...p, phone }))} />
          </FormField>

          {/* Module */}
          <FormField label="Módulo" isRequired error={errors.module} flex={1}>
            {isCustomModule ? (
              <Flex gap={2}>
                <Input
                  placeholder="Digite o nome do módulo"
                  value={form.module}
                  onChange={(e) => setForm((p) => ({ ...p, module: e.target.value }))}
                />
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsCustomModule(false)
                    setForm((p) => ({ ...p, module: '' }))
                  }}
                >
                  Voltar
                </Button>
              </Flex>
            ) : (
              <FormSelect
                value={form.module}
                onChange={(value) => {
                  if (value === '__other__') {
                    setIsCustomModule(true)
                    setForm((p) => ({ ...p, module: '' }))
                  } else {
                    setForm((p) => ({ ...p, module: value }))
                  }
                }}
                options={[
                  ...MODULE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
                  { value: '__other__', label: 'Outro...' }
                ]}
                placeholder="Selecione o módulo"
              />
            )}
          </FormField>
        </Flex>

        {/* `comment` tira a imagem que a barra local tinha: a imagem colada no editor vira um
            base64 dentro do HTML do chamado, e o anexo de verdade é o `FileDropzone` logo abaixo. */}
        <RichTextField
          label="Descrição do problema"
          isRequired
          error={errors.description}
          toolbar="comment"
          value={form.description}
          onChange={(description) => setForm((p) => ({ ...p, description }))}
          placeholder="Descreva o problema com detalhes, passos para reproduzir, etc."
        />

        <FileDropzone
          accept="attachment"
          maxSizeMB={SUPPORT_ATTACHMENT_MAX_MB}
          label="Anexos"
          preview="thumbnail"
          files={form.attachments ?? []}
          onChange={(attachments) => setForm((prev) => ({ ...prev, attachments }))}
          error={Object.entries(errors).find(([key]) => key.startsWith('attachments.'))?.[1]}
          isDisabled={saving}
        />
      </Flex>
    </FormModal>
  )
}
